import { takeLatest, put, select } from 'redux-saga/effects';
import { get } from 'lodash';
import { getDefaultStore } from 'jotai';
import { DATE_SHORTCUTS } from '@utils/constants';
import { calcShortcutInterval } from '@src/utils';

import * as actions from './actions';
import * as types from './types';
import selector from './selectors';
import { fetchData } from '../historicalRangeData/actions';
import { isFirstLoadMLAtom } from '../atom';
import { dimensionsListSelector } from '@store/common/dimensionList/selectors';
import { message } from 'antd';
import { DEFAULT_ML_DIMENSION } from '@utils/metricLens/mapping';
import { DEFAULT_FILTER } from '@src/utils/kpi/ei';
import { parseFilters } from '@src/store/common/persistence';
import { mlRouteKey } from '@utils/persistenceUtil';
import { persistenceAtom } from '@components/ActionToolbar/atoms';
import genApplyForAtomPersistence from '@store/common/initStore';

// Same store as Metric Lens dashboard (getDefaultStore) so we read/write the same currentKpiId etc.
const store = getDefaultStore();

function* initBase(action) {
  const isFirstLoadML = store.get(isFirstLoadMLAtom);
  // persistence action is common
  // ignore if triggered from another tab
  const key = get(action, ['response', 'key'], null);
  const { meta = {} } = action;
  const dimensionList = yield select(dimensionsListSelector);

  if (key !== mlRouteKey) {
    return;
  }
  const persistedML = get(action, ['response', 'configuration', 'ml'], {});

  const targetDimension = dimensionList.find((d) => d.groupBy === persistedML.selectedDimension);
  if (!targetDimension) {
    // this is happening when dimension is deleted/inactivated
    // so pick the default one as selected dimension
    persistedML.selectedDimension = DEFAULT_ML_DIMENSION;
    message.info(`previous dimension is removed, replaced by Assets`);
  }

  // fix CE-10666, When browser refresh, default to 6 hours for all the filters that were present in ML.
  if (persistedML?.appliedFilters) {
    if (isFirstLoadML) {
      const defaultShortcut = DATE_SHORTCUTS.last6Hours;
      const defaultInterval = {
        from: calcShortcutInterval(defaultShortcut)[0],
        to: calcShortcutInterval(defaultShortcut)[1],
      };
      persistedML.appliedFilters.forEach((it) => {
        it.shortcut = defaultShortcut;
        it.interval = defaultInterval;
      });
      store.set(isFirstLoadMLAtom, false);
    }

    persistedML.appliedFilters.forEach((it) => {
      const filter = get(it, 'filter', DEFAULT_FILTER);
      const parsedFilter = parseFilters(filter.rule);
      it.filter.rule = parsedFilter;
    });
  }

  yield put(actions.updateBase(persistedML));
  yield put(fetchData());

  // Restore Jotai atom state (e.g. currentKpiId) from persisted configuration
  const atomState = get(action, ['response', 'configuration', 'atomState']);
  if (atomState) {
    const applyAtomPersistence = genApplyForAtomPersistence({
      configuration: { atomState },
      pageKey: mlRouteKey,
    });
    applyAtomPersistence(store);
  }

  if ('doneCallback' in meta) {
    meta.doneCallback();
  }
}

function* triggerPersistence() {
  const baseState = yield select(selector);
  const atomState = store.get(persistenceAtom);
  yield put(actions.persistState({ ml: baseState, atomState }));
}

function* watchPersistedState() {
  yield takeLatest([types.UPDATE_PERSISTED_ML_STATE], initBase);
}

// actions to watch for persistence
function* watchActions() {
  yield takeLatest(
    [
      types.SET_SELECTED_METRICS,
      types.SET_SELECTED_DIMENSION,
      types.SET_SELECTED_FILTERS,
      types.SET_APPLIED_SORT,
      types.SET_SELECTED_INTERVAL,
      types.REMOVE_APPLIED_FILTER,
      types.SET_SELECTED_GRANULARITY,
      types.SET_TIMESERIES_MODE,
      types.SET_UNIT_MODE,
      types.SET_SELECTED_SHORTCUT,
      types.SET_LIVE_MODE,
    ],
    triggerPersistence,
  );
}

export default [watchPersistedState, watchActions];
